import React, { useMemo, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';

const TEXT_FIELDS = ['marka', 'partCode', 'partDescTr', 'partDescEn', 'uom', 'price', 'currency'];

const MatnrPartPriceTable = ({ 
    items, 
    totalCount, 
    loading, 
    lazyParams, 
    onPage, 
    activeFilters, 
    onFiltersChange, 
    sortField, 
    sortOrder, 
    onSortChange, 
    isDomestic 
}) => {
    const { th } = useLanguage();

    // Dil seçeneğine göre filtreleme dropdown opsiyonları
    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const tableFilters = useMemo(
        () => ({
            marka: { value: activeFilters?.marka ?? null, matchMode: activeFilters?.markaMatchMode ?? FilterMatchMode.CONTAINS },
            partCode: { value: activeFilters?.partCode ?? null, matchMode: activeFilters?.partCodeMatchMode ?? FilterMatchMode.CONTAINS },
            partDescTr: { value: activeFilters?.partDescTr ?? null, matchMode: activeFilters?.partDescTrMatchMode ?? FilterMatchMode.CONTAINS },
            partDescEn: { value: activeFilters?.partDescEn ?? null, matchMode: activeFilters?.partDescEnMatchMode ?? FilterMatchMode.CONTAINS },
            uom: { value: activeFilters?.uom ?? null, matchMode: activeFilters?.uomMatchMode ?? FilterMatchMode.CONTAINS },
            price: { value: activeFilters?.price ?? null, matchMode: activeFilters?.priceMatchMode ?? FilterMatchMode.CONTAINS },
            currency: { value: activeFilters?.currency ?? null, matchMode: activeFilters?.currencyMatchMode ?? FilterMatchMode.CONTAINS }
        }),
        [activeFilters]
    );


    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const tableItems = useMemo(() => {
        return (items ?? []).map((item, index) => ({
            ...item,
            rowKey: `${item.kunnr}-${item.partCode}-${index}`
        }));
    }, [items]);

    const createTextColumn = useCallback((field, header, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return (
            <Column
                key={field}
                sortable={sortable}
                field={field}
                header={header}
                style={{ minWidth }}
                body={body}
                filter
                filterField={field}
                filterPlaceholder={th('common.search')}
                showFilterMenu={true}
                filterMatchModeOptions={textMatchModeOptions}
                showClearButton={false}
            />
        );
    }, [textMatchModeOptions, th]);

    return (
        <DataTable
            value={tableItems}
            totalRecords={totalCount}
            className="p-datatable-gridlines"
            showGridlines
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows || 20}
            onPage={onPage}
            dataKey="rowKey"
            loading={loading}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 20, 50, 100]}
            tableStyle={{ minWidth: '80rem' }}
            onSort={onSortChange}
            sortField={sortField}
            sortOrder={sortOrder}
            sortMode="single"
        >
            {createTextColumn('marka', th('matnrPartsPriceReport.columns.marka'), { minWidth: '140px' })}
            {createTextColumn('partCode', th('matnrPartsPriceReport.columns.partCode'), { minWidth: '140px' })}
            
            {isDomestic
                ? createTextColumn('partDescTr', th('matnrPartsPriceReport.columns.partDescTr'), { minWidth: '140px' })
                : createTextColumn('partDescEn', th('matnrPartsPriceReport.columns.partDescEn'), { minWidth: '140px' })
            }
            
            {createTextColumn('uom', th('matnrPartsPriceReport.columns.uom'), { minWidth: '120px' })}
            {createTextColumn('price', th('matnrPartsPriceReport.columns.price'), { minWidth: '120px' })}
            {createTextColumn('currency', th('matnrPartsPriceReport.columns.currency'), { minWidth: '120px' })}
        </DataTable>
    );
};

export default MatnrPartPriceTable;